import React from 'react'
import { Box, Link, Stack, SvgIcon, Tooltip, Typography, TooltipProps } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import type { LinkingItem } from '../../../../../components/Linking/Linking.types'
import useActivityStatuses from '../../../../../shared/hooks/useActivityStatuses'

type CellTooltipType = 'evidence' | 'activity' | 'valueMessage' | 'comment'

type CellTooltipProps = {
    type: CellTooltipType
    items: LinkingItem[]
    emptyLabel: string
    isLoading?: boolean
    children: React.ReactElement
}

const getStyledTooltipComponentsProps = (): TooltipProps['componentsProps'] => {
    return {
        tooltip: {
            sx: {
                borderRadius: '12px',
                bgcolor: 'figma.grayscale.tier7',
                boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.20)',
                display: 'inline-flex',
                padding: '12px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: '10px',
                // Text specs
                color: 'figma.grayscale.textHier2',
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '150%', // 21px
                pointerEvents: 'auto',
            },
        },
        arrow: {
            sx: {
                color: 'figma.grayscale.tier7',
            },
        },
    }
}

const getItemLabel = (type: CellTooltipType, item: LinkingItem): string => {
    switch (type) {
        case 'evidence':
        case 'valueMessage':
        case 'activity':
        default: {
            const anyItem = item as any
            return (anyItem?.citation || anyItem?.title || item?.name || item?._id || '').toString()
        }
    }
}

const getPathForType = (type: CellTooltipType): string => {
    switch (type) {
        case 'evidence':
            return '/evidence'
        case 'activity':
            return '/activities'
        case 'valueMessage':
            return '/narratives'
        default:
            return '/'
    }
}

const getItemLinkingProps = (type: CellTooltipType, item: LinkingItem) => {
    return {
        pathname: getPathForType(type),
        autoOpenData: {
            isAutoOpenPanel: true,
            autoOpenData: {
                _id: item._id,
                ...(item.extras || {}),
            },
            previousDataStack: [{ path: '/tpp', data: {} }],
        },
    }
}

const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

export default function CellTooltip({ type, items, emptyLabel, isLoading = false, children }: CellTooltipProps) {
    const { getActivityStatusDetails } = useActivityStatuses()
    const hasItems = Array.isArray(items) && items.length > 0

    const renderNumberedTooltip = () => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    maxHeight: 200,
                    overflowY: 'auto',
                    scrollbarGutter: 'stable',
                }}
            >
                {items.map((item, idx) => (
                    <Link
                        key={item?._id || idx}
                        component={RouterLink}
                        to={getItemLinkingProps(type, item)}
                        underline="hover"
                        color="inherit"
                        sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: '150%',
                            cursor: 'pointer',
                        }}
                    >
                        {`${idx + 1}. ${getItemLabel(type, item)}`}
                    </Link>
                ))}
            </Box>
        )
    }

    const renderActivityTooltip = () => {
        return (
            <Stack gap={2} sx={{ maxHeight: 200, overflowY: 'auto', scrollbarGutter: 'stable' }}>
                {items.map((item, idx) => {
                    const extras = item.extras as any
                    const hasStatus = extras?.status != null
                    const statusDetails = hasStatus ? getActivityStatusDetails(extras.status) : null
                    const StatusIcon = statusDetails?.icon

                    return (
                        <Stack key={item._id} gap={0.5}>
                            <Link
                                component={RouterLink}
                                to={getItemLinkingProps(type, item)}
                                underline="hover"
                                color="inherit"
                                sx={{
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '150%',
                                    cursor: 'pointer',
                                }}
                            >
                                {`${idx + 1}. ${getItemLabel(type, item)}`}
                            </Link>
                            {extras?.endDate && (
                                <Typography variant="14x400" color="inherit">
                                    <strong>End date:</strong> {formatDate(extras.endDate)}
                                </Typography>
                            )}
                            {hasStatus && statusDetails && StatusIcon && (
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Typography variant="14x400" color="inherit">
                                        <strong>Status:</strong>
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={0.5}
                                        sx={{
                                            bgcolor: 'figma.grayscale.tier6',
                                            borderRadius: '8px',
                                            px: 1,
                                            py: 0.25,
                                        }}
                                    >
                                        <SvgIcon component={StatusIcon} inheritViewBox sx={{ fontSize: '14px' }} />
                                        <Typography variant="14x400" color="inherit">
                                            {statusDetails.name}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    )
                })}
            </Stack>
        )
    }

    const renderTooltipContent = () => {
        if (type === 'activity') {
            return renderActivityTooltip()
        }
        return renderNumberedTooltip()
    }

    return (
        <Tooltip
            title={isLoading ? 'Loading...' : hasItems ? renderTooltipContent() : emptyLabel}
            arrow={hasItems}
            componentsProps={hasItems ? getStyledTooltipComponentsProps() : undefined}
            disableInteractive={!hasItems}
            enterDelay={200}
            leaveDelay={200}
        >
            {children}
        </Tooltip>
    )
}
